import { useContent } from "../hooks/useContent";
import { useInView } from "../hooks/useInView";
import {
  Zap,
  Truck,
  IndianRupee,
  Leaf,
  ShieldCheck,
  CalendarClock,
} from "lucide-react";

const iconMap = {
  zap: Zap,
  truck: Truck,
  indianRupee: IndianRupee,
  leaf: Leaf,
  shieldCheck: ShieldCheck,
  calendarClock: CalendarClock,
};

const gradients = [
  "from-lime-50 to-orange-50",
  "from-sky-50 to-cyan-50",
  "from-emerald-50 to-green-50",
  "from-sky-50 to-blue-50",
  "from-violet-50 to-purple-50",
  "from-rose-50 to-pink-50",
];

const iconColors = [
  "text-lime-500",
  "text-sky-600",
  "text-emerald-500",
  "text-sky-500",
  "text-violet-500",
  "text-rose-500",
];

export const WhyChooseUs = () => {
  const [ref, isInView] = useInView();
  const { getSection } = useContent();
  const whyChooseUs = getSection("whyChooseUs") || [];

  return (
    <section className="py-16 lg:py-20 bg-white relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-sky-50/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4 pointer-events-none" />

      {/* Soft soap-bubble graphic accents */}
      <div className="absolute top-12 right-16 w-3 h-3 bg-sky-200/60 rounded-full animate-float pointer-events-none" style={{ animationDelay: "0.5s" }} />
      <div className="absolute top-32 right-32 w-2 h-2 bg-lime-300/60 rounded-full animate-float pointer-events-none" style={{ animationDelay: "1.2s" }} />
      <div className="absolute bottom-20 right-1/3 w-2.5 h-2.5 bg-sky-300/60 rounded-full animate-float pointer-events-none" style={{ animationDelay: "1.8s" }} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div ref={ref}>
            <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-4">
              Why Blitzwash
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-5">
              Your Clothes Deserve
              <br />
              <span className="text-sky-600">Better Care</span>
            </h2>
            <p className="text-lg text-slate-500 leading-relaxed mb-8">
              We believe clothes belong to you, not the laundry shop. That's why
              we're revolutionizing the laundry industry with speed, quality, and
              care that's unmatched.
            </p>

            {/* Key highlight */}
            <div className="bg-gradient-to-r from-sky-50 to-emerald-50 border border-sky-100 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-lime-500" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1">
                    The 24-Hour Promise
                  </h4>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    We pick up your laundry and deliver it back fresh, clean, and
                    perfectly folded — all within 24 hours. No more waiting days
                    for your own clothes.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Grid */}
          <div
            className={`grid sm:grid-cols-2 gap-5 transition-all duration-700 ${
              isInView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            {whyChooseUs.map((item, i) => {
              const Icon = iconMap[item.iconKey];
              return (
                <div
                  key={i}
                  className={`rounded-2xl p-6 bg-gradient-to-br ${gradients[i]} border border-slate-100/80 hover:shadow-md transition-all duration-300 group`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <Icon
                    className={`w-7 h-7 ${iconColors[i]} mb-4 group-hover:scale-110 transition-transform duration-300`}
                  />
                  <h3 className="text-base font-bold text-slate-900 mb-1.5">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
